import { get } from "lodash";
import rill from "rill";
import ensureCoach from "../../middleware/ensure-coach";
import ensureCoachUpgrade from "../../middleware/ensure-coach-upgrade";
import ensurePaid from "../../middleware/ensure-paid";
import ensureSetup from "../../middleware/ensure-setup";
import ensureUser from "../../middleware/ensure-user";
import loadActivityFeed from "../../middleware/loadActivityFeed";
import { getNewsLoaderArgs } from "../../parts/news-drawer/constants";
import getProgramEditor from "../athlete-portal/programs/program-editor.controller";
import getProgramOverview from "../athlete-portal/programs/program-overview/program-overview.controller";
import getProgramPreview from "../dashboard/program-library/program-preview.controller";
import { getUserToRequest } from "../dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import AccessRequests from "./access-requests";
import AllTemplates from "./all-templates";
import Athletes from "./athletes";
import ChallengeReport from "./challenge-report";
import Chat from "./chat";
import CoachingDashboard from "./coaching-dashboard";
import CommunityChat from "./community-chat";
import CustomWorkflows from "./custom-workflows";
import DoorAccess from "./door-access";
import getMealPlanEditor from "./edit-meal-plan/edit-meal-plan.controller";
import ExerciseLibrary from "./exercise-library";
import GymMembers from "./gym-members";
import Income from "./income";
import MasterFoodLibrary from "./master-food-library";
import MembershipList from "./membership-list";
import MSBUniversity from "./msb-university";
import Notifications from "./notifications";
import PlanManager from "./plan-manager";
import Scheduling from "./scheduling";
import Services from "./services";
import Settings from "./settings";
import Store from "./store";
import SubscriberList from "./subscriber-list";
import Support from "./support";
import Tasks from "./tasks";
import getTeams from "./teams/teams.controller";
import Widgets from "./widgets";

const CoachingPortal = (app) => {
  app.get("/coaching-portal").at(
    "/coaching-portal/*",
    ensureUser,
    ensureCoach,
    ensureCoachUpgrade,
    ensurePaid,
    loadActivityFeed,
    rill()
      .get(ensureSetup)
      .use(async ({ load, req }, next) => {
        await Promise.all([
          load("listNotifications", { limit: 10 }),
          load("unreadNewsCount"),
          ...(get(req.query, "modal.type") === "whats-new"
            ? [load("listNews", getNewsLoaderArgs(req.query.newsSkip))]
            : []),
        ]);
        return next();
      })

      .setup((app) => {
        app.get("/", ({ res, locals }) => {
          const { activeUser, organizationOwner } = locals;
          const userToRequest = getUserToRequest(activeUser, organizationOwner);
          const coachOrganizationPermissions =
            locals?.activeUser?.organizationPermissions || [];

          // First look for the permissions that allow the coach to acces to the store -
          // - in the ORGANIZATION permissions
          // These are by now "Templates" and "PUSH"
          const organizationStorePermissions =
            locals?.organizationPermissions?.filter((permission) =>
              ["PUSH"].includes(permission.permission)
            ) || [];

          const isAdminOrOwner =
            locals?.organizationRoleName === "Owner" ||
            locals?.organizationRoleName === "Admin";

          // Then, in the permisisons of the COACH, find the a permissions that matches with the ones that
          // allow the coach to access to the store
          const coachHasStoreAccess = locals?.activeUser?.organizationUserStatus
            ? isAdminOrOwner ||
              organizationStorePermissions?.some((storePermission) =>
                coachOrganizationPermissions.includes(storePermission?._id)
              )
            : true;

          return res.redirect(
            userToRequest?.pushAccountInfo &&
              coachHasStoreAccess &&
              !(process.window && window?.innerWidth <= 960)
              ? "/coaching-portal/store"
              : "/coaching-portal/athletes"
          );
        });
      })
      .setup(Athletes)
      .setup(AllTemplates)
      .setup(Settings)
      .setup(Store)
      .setup(Chat)
      .setup(CommunityChat)
      .setup(MasterFoodLibrary)
      .setup(Income)
      .setup(SubscriberList)
      .setup(CoachingDashboard)
      .setup(Tasks)
      .setup(ExerciseLibrary)
      .setup(MSBUniversity)
      .setup(Widgets)
      .setup(Scheduling)
      .setup(CustomWorkflows)
      .setup(PlanManager)
      .setup(MembershipList)
      .setup(DoorAccess)
      .setup(Notifications)
      .setup(Support)
      .setup(Services)
      .setup(AccessRequests)
      .setup(GymMembers)
      .setup(ChallengeReport)
      .setup((app) => {
        app
          .get("/templates/program-overview/:cycleId", getProgramOverview)
          .get("/templates/program-preview/:cycleId", getProgramPreview)
          .get("/store/program-preview/:cycleId", getProgramPreview)
          .get("/store/teams/:cycleId", getTeams)
          .get("/templates/teams/:cycleId", getTeams)
          .get("/templates/edit-program/:cycleId", getProgramEditor)
          .get("/store/edit-program/:cycleId", getProgramEditor)
          .get("/store/edit-program/:cycleId", getProgramEditor)
          .get("/templates/edit-meal-plan", getMealPlanEditor);
      })
  );
};

export default CoachingPortal;
